import { MantineProvider, createTheme } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
// Tailwind CSS is now imported only in the landing page component
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';

// Create a special function to check if the current page is one that doesn't need authentication
const isAuthExemptPath = (path: string): boolean => {
  // Extract the base path without query parameters to handle URLs with parameters
  const basePath = path.split('?')[0];
  
  // These paths should be accessible without authentication
  return basePath === '/' ||
         basePath === '/signin' ||
         basePath === '/register' ||
         basePath === '/resetpassword' ||
         basePath.startsWith('/setpassword/') ||
         basePath === '/terms' ||
         basePath === '/privacy' ||
         basePath === '/cookies' ||
         basePath === '/data-protection' ||
         basePath === '/ai-ethics' ||
         basePath === '/how-it-works' ||
         basePath === '/benefits' ||
         basePath === '/faq' ||
         basePath === '/account-deletion';
};

const medplum = new MedplumClient({
  // To run FooMedical locally, you can set the baseURL in this constructor
  // baseUrl: http://localhost:8103
  baseUrl: "https://api.progressnotes.app", // TODO: Change to localhost:8103 when running locally    
  // Note: Token lifetimes must be configured in the ClientApplication resource
  // Setting a longer refresh grace period (10 minutes)
  refreshGracePeriod: 600000,
  onUnauthenticated: () => {
    // Get the current path
    const currentPath = window.location.pathname;
    console.log('Medplum onUnauthenticated triggered on path:', currentPath);
    
    // Don't redirect for paths that don't require authentication
    if (isAuthExemptPath(currentPath)) {
      console.log('Path is exempt from authentication, not redirecting');
      return;
    }
    
    // For all other paths, redirect to home
    console.log('Redirecting to home due to unauthenticated state');
    window.location.href = '/';
  },
});

const theme = createTheme({
  primaryColor: 'teal',
  primaryShade: 6,
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.875rem',
    md: '0.875rem',
    lg: '1rem',
    xl: '1.125rem',
  },
  components: {
    Container: {
      defaultProps: {
        size: 1200,
      },
    },
    Button: {
      defaultProps: {
        color: 'teal',
        variant: 'filled',
      },
      styles: {
        root: {
          backgroundColor: '#14b8a6',
          borderColor: '#14b8a6',
          color: 'white',
          '&:hover': {
            backgroundColor: '#0f9988',
            transform: 'translateY(-1px)',
          },
          '&[data-variant="subtle"]': {
            backgroundColor: 'transparent',
            color: '#14b8a6',
            '&:hover': {
              backgroundColor: 'rgba(20, 184, 166, 0.1)',
            }
          }
        },
      },
    },
  },
});

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <StrictMode>
    <BrowserRouter>
      <MedplumProvider medplum={medplum}>
        <MantineProvider theme={theme}>
          <Notifications />
          <App />
        </MantineProvider>
      </MedplumProvider>
    </BrowserRouter>
  </StrictMode>
);